import {
	ContentWithSidebar,
	ContextActionBar,
	Dialog,
	FormattedText,
	getPropertySortText,
	NoResults,
	OptionsMenu,
	OptionsMenuItem,
	PageContentTitle,
	SearchInputForm,
	SearchPlaceholder,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { CategoryFilter, CategoryFilterHandle } from "./CategoryFilter";
import {
	EMPTY_ARRAY,
	getNumericEnumValues,
	isNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { DataSourceSortDirection, nativeSortFunction } from "client/Data/sorting";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { AppIconModel } from "client_legacy/AppIcon/common";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	AppIconCategoryKind,
	AvailableIcon,
	deleteTileIcon,
	getAvailableIcons
} from "client/Api/AppIcon/appIcon";
import { contains } from "client/Data/searching";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	areTheSameAppIcons,
	IconList,
	IconListHandle
} from "./IconList";
import styles from "./ChooseAppIconDialog.module.less";
import { useEditAppIconDialog } from "./EditAppIconDialog";
import { HttpRequestError } from "client/Api/httpClient";
import { ClientValidationErrorModel } from "client/Api/common";
import { newGuid } from "client_legacy/ndaw/guid";
import classNames from "classnames";

export type ChooseAppIconDialogProps = {
	onSelected(icon: AppIconModel): void;
	onClose(): void;
};

export const ChooseAppIconDialog = (props: ChooseAppIconDialogProps): ReactElement => {
	const {
		onSelected,
		onClose: onCloseFromProps
	} = props;

	const [icons, setIcons] = useState<readonly AvailableIcon[]>(EMPTY_ARRAY);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [selectedCategory, setSelectedCategory] = useState<AppIconCategoryKind | null>(null);
	const [canUploadAndManage, setCanUploadAndManage] = useState<boolean>(false);
	const [sortDirection, setSortDirection] = useState<DataSourceSortDirection>(DataSourceSortDirection.Ascending);
	const [selectedIcon, setSelectedIcon] = useState<AvailableIcon | null>(null);

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const onClose: typeof onCloseFromProps = useStableImperativeCallback(onCloseFromProps);
	const listRef: RefObject<IconListHandle | null> = useRef<IconListHandle>(null);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [editAppIconDialog, openEditAppIconDialog] = useEditAppIconDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const categoryFilterRef: RefObject<CategoryFilterHandle | null> = useRef<CategoryFilterHandle>(null);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const listContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const {
		currentItems
	} = useIconsDataSource(
		icons,
		{
			searchTerm: searchTerm.value,
			sorting: {
				direction: sortDirection,
				propertyName: "name"
			},
			filtering: {
				selectedCategory
			}
		}
	);

	const categoryChangeHandler = useCallback(
		(selectedCategory: AppIconCategoryKind | null): void => {
			setSelectedCategory(selectedCategory);
			setSelectedIcon(null);
		},
		[]
	);

	const uploadIconHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<AvailableIcon> = await openEditAppIconDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			const newIcon: AvailableIcon = result.result;

			setIcons(icons => {
				return [
					...icons,
					newIcon
				];
			});

			setSelectedCategory(newIcon.categoryKind);
			categoryFilterRef.current
				?.scrollCategoryIntoView(newIcon.categoryKind);

			setSelectedIcon(newIcon);
			listRef.current
				?.scrollItemIntoView(newIcon);
		},
		[openEditAppIconDialog]
	);

	const editIconHandler = useCallback(
		async (icon: AvailableIcon): Promise<void> => {
			const result: TryResult<AvailableIcon> = await openEditAppIconDialog({
				icon
			});

			if (!result.wasSuccessful) {
				return;
			}

			const newIcon: AvailableIcon = result.result;

			setIcons(icons => {
				return icons.map(
					x => areTheSameAppIcons(x, icon)
						? newIcon
						: x
				);
			});
			setSelectedIcon(newIcon);
			listRef.current
				?.scrollItemIntoView(newIcon);
		},
		[openEditAppIconDialog]
	);

	const deleteIconHandler = useCallback(
		async (icon: AvailableIcon): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: (
					<FormattedText
						text={localize("_AreYouSureYouWantToDeleteTheIcon")}
						thingsToReplace={[
							{
								what: "{{IconName}}",
								filler: icon.name
							}
						]}
					/>
				),
				confirmText: localize("_Delete"),
				trackingId: "delete-app-icon-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			try {
				setIsBusy(true);

				await deleteTileIcon(icon.id, globalCancellation);

				setIcons((x) => {
					const copy: AvailableIcon[] = [...(x ?? [])];
					const index: number = copy.findIndex(x => x.id === icon.id && x.type === icon.type);
					if (index !== -1) {
						copy.splice(index, 1);
					}
					return copy;
				});

				setSelectedIcon(null);
				setIsBusy(false);
			} catch (e) {
				let handled: boolean = false;
				if (e instanceof HttpRequestError && e.statusCode === 400) {
					try {
						const errorResponse: ClientValidationErrorModel[] = e.readJson<ClientValidationErrorModel[]>();
						alertManager.showError(errorResponse[0].errorMessage);
						handled = true;
					} catch (error) {
						console.error(error);
					}
				}

				if (!handled) {
					alertManager.showError(localize("_ChangesNotSaved"));
					console.error(e);
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const chooseIconHandler = useCallback(
		(): void => {
			if (!selectedIcon) {
				console.error("No icon is selected but choose icon is clicked");
				return;
			}

			const {
				id,
				type,
				renderType,
				url
			} = selectedIcon;

			onSelected({
				id,
				type,
				renderType,
				url
			});
		},
		[
			onSelected,
			selectedIcon
		]
	);

	const actions = useMemo(
		(): readonly Action<ChooseIconActionContext>[] => getActions(uploadIconHandler),
		[uploadIconHandler]
	);

	const actionContext = useMemo(
		(): ChooseIconActionContext => ({
			canUploadAndManage
		}),
		[canUploadAndManage]
	);

	const categories = useMemo(
		(): readonly IconCategoryItem[] => getCategoryItems(),
		[]
	);

	const itemActions = useMemo(
		(): (readonly Action<AvailableIcon>[]) | undefined => {
			if (!canUploadAndManage) {
				return undefined;
			}

			return listItemActions({
				edit: (icon) => editIconHandler(icon),
				delete: (icon) => deleteIconHandler(icon)
			});
		},
		[
			canUploadAndManage,
			deleteIconHandler,
			editIconHandler
		]
	);

	useEffect(
		() => {
			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);
					const { canUploadAndManage, icons } = await getAvailableIcons(signal);
					setIcons(icons);
					setCanUploadAndManage(canUploadAndManage);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, onClose]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(selectedCategory);
			listContainerRef.current
				?.scrollTo({ top: 0 });
		},
		[selectedCategory]
	);

	const renderItems = (): ReactElement => {
		if (currentItems.length > 0) {
			return (
				<IconList
					ref={listRef}
					icons={currentItems}
					selectedIcon={selectedIcon}
					onSelectIcon={setSelectedIcon}
					actions={itemActions}
				/>
			);
		}

		if (searchTerm.value) {
			return (
				<NoResults
					icon="magnifying-glass"
					title={localize("_NoSearchResults")}
					message={localize("_PleaseTrySearchingWithAnotherTerm")}
					height="fill"
				/>
			);
		}

		if (selectedCategory === AppIconCategoryKind.Custom) {
			return (
				<NoResults
					icon="wrench"
					title={localize("_NoCustomIconsToShow")}
					message={localize("_ThereAreCurrentlyNoCustomIconsYet")}
					height="fill"
				/>
			);
		}

		return (
			<NoResults
				icon="window-restore"
				title={localize("_NoIconsYet")}
				message={localize("_ThereAreCurrentlyNoIconsYet")}
				height="fill"
			/>
		);
	};

	if (editAppIconDialog) {
		return editAppIconDialog;
	}

	return (
		<>
			<Dialog
				trackingId="choose-icon-dialog"
				hasBusyIndicator={isBusy}
				onClose={onClose}
				variant="left-slider"
			>
				<Dialog.Header
					title={localize("_ChooseAnIcon")}
					onClose={onClose}
					hasBorder
				/>
				<Dialog.Body goesEdgeToEdge>
					<ContentWithSidebar>
						<ContentWithSidebar.Sidebar>
							<CategoryFilter
								categories={categories}
								selectedCategory={selectedCategory}
								onChange={categoryChangeHandler}
								ref={categoryFilterRef}
							/>
						</ContentWithSidebar.Sidebar>
						<ContentWithSidebar.Content hasNoPadding={isMobile}>
							<div
								className={classNames(
									styles.content,
									{
										[styles.content_hasActionBar]: isMobile
									}
								)}
							>
								{isMobile && (
									<ContextActionBar
										actions={actions}
										context={actionContext}
										trackingId="choose-icon-action-bar"
										hasBorderBottom
									/>
								)}
								<div className={styles.title}>
									<PageContentTitle
										title={isNil(selectedCategory)
											? localize("_AllIcons")
											: localize(iconCategoryToLocalizationKeyMap[selectedCategory])
										}
										actions={actions}
										actionContext={actionContext}
									/>
								</div>
								<div className={styles.searchAndSort}>
									<SearchInputForm
										value={searchTerm}
										onChange={(value, version) => setSearchTerm({value, version})}
										height="large"
										icon="filter"
										placeholder={(
											<SearchPlaceholder
												kind="filter"
												properties={[
													localize("_Name")
												]}
											/>
										)}
										trackingId="search-input"
									/>
									<OptionsMenu
										value={sortDirection}
										onChange={setSortDirection}
										options={sortOptions}
										title={localize("_SortBy")}
										buttonSize="large"
										icon="arrow-up-arrow-down"
										trackingId="sort-direction-option"
									/>
								</div>
								<div
									ref={listContainerRef}
									className={styles.listContainer}
								>
									{renderItems()}
								</div>
							</div>
						</ContentWithSidebar.Content>
					</ContentWithSidebar>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_ChooseIcon")}
							onClick={chooseIconHandler}
							isDisabled={!selectedIcon}
							trackingId="choose-icon"
						/>
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{confirmationDialog}
		</>
	);
};
ChooseAppIconDialog.displayName = "ChooseAppIconDialog";

type FilterState = {
	selectedCategory: AppIconCategoryKind | null;
};

function applyFilter(
	icons: readonly AvailableIcon[],
	filter: FilterState
): readonly AvailableIcon[] {
	return isNil(filter.selectedCategory)
	? icons
	: icons.filter(
		x => x.categoryKind === filter.selectedCategory
	);
}

const useIconsDataSource = createLocalDataSource<AvailableIcon, FilterState>()
	.setConfiguration({
		search: {
			doesMatchItem: (icon, searchTerm) => contains(icon.name, searchTerm)
		},
		filtering: {
			applyFilter
		}
	})
	.getHook();

export const iconCategoryToLocalizationKeyMap: Record<AppIconCategoryKind, LocalizationKey> = {
	[AppIconCategoryKind.Adobe]: "_Adobe",
	[AppIconCategoryKind.Microsoft]: "_Microsoft",
	[AppIconCategoryKind.Brands]: "_Brands",
	[AppIconCategoryKind.Devices]: "_Devices",
	[AppIconCategoryKind.Finance]: "_Finance",
	[AppIconCategoryKind.Social]: "_Social",
	[AppIconCategoryKind.Healthcare]: "_Healthcare",
	[AppIconCategoryKind.Education]: "_Education",
	[AppIconCategoryKind.Transport]: "_Transport",
	[AppIconCategoryKind.Google]: "_Google",
	[AppIconCategoryKind.Weather]: "_Weather",
	[AppIconCategoryKind.BusinessApps]: "_BusinessApps",
	[AppIconCategoryKind.Documents]: "_Documents",
	[AppIconCategoryKind.CRM]: "_CRM",
	[AppIconCategoryKind.Development]: "_Development",
	[AppIconCategoryKind.Browsers]: "_Browsers",
	[AppIconCategoryKind.Time]: "_Time",
	[AppIconCategoryKind.Sounds]: "_Sounds",
	[AppIconCategoryKind.Other]: "_Other",
	[AppIconCategoryKind.Custom]: "_Custom"
};

const sortOptions: readonly OptionsMenuItem<DataSourceSortDirection>[] = [
	{
		value: DataSourceSortDirection.Ascending,
		text: getPropertySortText("_Name", "_AToZ")
	},
	{
		value: DataSourceSortDirection.Descending,
		text: getPropertySortText("_Name", "_ZToA")
	}
];

type ChooseIconActionContext = {
	canUploadAndManage: boolean;
};

function getActions(
	uploadIcon: () => void
): readonly Action<ChooseIconActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "upload-icon",
			canExecute: (c) => c.canUploadAndManage,
			execute: uploadIcon,
			title: localize("_UploadIcon"),
			icon: "upload"
		}
	];
}

export type IconCategoryItem = {
	id: AppIconCategoryKind | null;
	name: string;
};

export const allIconsCategory: IconCategoryItem = {
	id: null,
	name: localize("_AllIcons")
};

function getCategoryItems(): readonly IconCategoryItem[]{
	const restOfCategories: IconCategoryItem[] = getNumericEnumValues(AppIconCategoryKind)
		.map(x => {
			const result: IconCategoryItem = {
				id: x,
				name: localize(iconCategoryToLocalizationKeyMap[x])
			};

			return result;
		})
		.sort((a, b) => {
			if (a.id === AppIconCategoryKind.Custom && b.id === AppIconCategoryKind.Custom) {
				return 0;
			}

			if (a.id === AppIconCategoryKind.Custom) {
				return 1;
			}

			if (b.id === AppIconCategoryKind.Custom) {
				return -1;
			}

			return nativeSortFunction(
				a.name,
				b.name,
				DataSourceSortDirection.Ascending
			);
		});

	return [
		allIconsCategory,
		...restOfCategories
	];
}

type ItemActionHandlers = {
	edit: (icon: AvailableIcon) => void;
	delete: (icon: AvailableIcon) => void;
};

function listItemActions(
	handlers: ItemActionHandlers
): readonly Action<AvailableIcon>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			icon: "pen",
			title: localize("_Edit"),
			canExecute: (x) => x.categoryKind === AppIconCategoryKind.Custom,
			execute: (x) => handlers.edit(x)
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			icon: "trash-can",
			title: localize("_Delete"),
			canExecute: (x) => x.categoryKind === AppIconCategoryKind.Custom,
			execute: (x) => handlers.delete(x)
		}
	];
}
